<template>
  <v-card>
    <v-card-item>
      <v-card-title>
        <sf-heading>{{ $t('order.orderNumbers') }}</sf-heading>
      </v-card-title>
    </v-card-item>
    <v-card-text>
      <v-row density="comfortable" justify="center">
        <v-col :md="showCustomerOrderId ? 4 : 6" cols="12" sm="6">
          <sf-text-field
            ref="customer-number"
            :label="$t('clientNumber')"
            :aria-label="$t('clientNumber')"
            :model-value="customerNumber"
            hide-details
            readonly
            autocomplete="off"
          />
        </v-col>
        <v-col :md="showCustomerOrderId ? 4 : 6" cols="12" sm="6">
          <sf-text-field
            v-if="showOrderNumber"
            ref="order-number"
            v-model="orderNumberValue"
            v-debounce="onOrderNumberChanged"
            :label="orderNumberLabel"
            :aria-label="orderNumberLabel"
            :readonly="readOnly || !isCustomOrderNumberEnabled"
            :rules="[requiredRule, orderNumberRule]"
            :error-messages="orderNumberError"
            autocomplete="off"
            :maxlength="ORDER_NUMBER_MAX_LENGTH"
            :hint="
              orderNumberValue.length >= ORDER_NUMBER_MAX_LENGTH
                ? $t('maxLength', [ORDER_NUMBER_MAX_LENGTH])
                : ''
            "
            persistent-hint
          />
          <sf-text-field
            v-else
            class="input-italic"
            :model-value="$t('order.orderNumberGeneratedAfterOrder')"
            :label="$t('order.orderNumber')"
            :aria-label="$t('order.orderNumber')"
            readonly
            autocomplete="off"
          />
        </v-col>
        <v-col v-if="showCustomerOrderId" cols="12" md="4" sm="6">
          <sf-text-field
            ref="customer-order-id"
            v-model="customerOrderIdValue"
            v-debounce="onCustomerOrderIdChanged"
            :label="customerOrderLabel"
            :aria-label="customerOrderLabel"
            :readonly="readOnly"
            :rules="customerOrderIdRequired ? [requiredRule, orderNumberRule] : [orderNumberRule]"
            autocomplete="off"
            :maxlength="ORDER_NUMBER_MAX_LENGTH"
            :hint="
              customerOrderIdValue.length >= ORDER_NUMBER_MAX_LENGTH
                ? $t('maxLength', [ORDER_NUMBER_MAX_LENGTH])
                : ''
            "
            persistent-hint
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import SfTextField from '@/components/input/SfTextField.vue'
import SfHeading from '@/components/text/SfHeading.vue'
import { ORDER_NUMBER_MAX_LENGTH } from '@/modules/order/types'
import useCheckout from '@/modules/order/useCheckout'
import { OrderNumberEvents } from '@/modules/tracking/events/checkout'
import { trackEvent } from '@/modules/tracking/useTracking'
import { t } from '@/plugins/i18n'
import { requiredRule, orderNumberRule } from '@/rules'
import { storeToRefs } from 'pinia'
import { computed, PropType, ref, watch } from 'vue'

const props = defineProps({
  customerNumber: {
    type: String,
    required: true
  },
  orderNumber: {
    type: String,
    default: ''
  },
  customerOrderId: {
    type: String,
    default: ''
  },
  showCustomerOrderId: {
    type: Boolean,
    default: false
  },
  customerOrderIdRequired: {
    type: Boolean,
    default: false
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  showOrderNumber: {
    type: Boolean,
    default: true
  },
  isCustomOrderNumberEnabled: {
    type: Boolean,
    required: true
  },
  events: {
    type: Object as PropType<OrderNumberEvents>,
    default: undefined
  }
})
const emit = defineEmits<{
  orderNumberChanged: [string]
  customerOrderIdChanged: [string]
}>()

const { orderNumberError } = storeToRefs(useCheckout())
const orderNumberValue = ref(props.orderNumber)
const customerOrderIdValue = ref(props.customerOrderId ?? '')

const orderNumberLabel = computed(
  () => t('order.orderNumber') + (props.readOnly || !props.isCustomOrderNumberEnabled ? '' : ' *')
)
const customerOrderLabel = computed(
  () => t('customerOrderId') + (props.customerOrderIdRequired ? ' *' : '')
)

const onOrderNumberChanged = () => {
  if (!props.readOnly && props.isCustomOrderNumberEnabled) {
    useCheckout()
      .validateOrderNumber(orderNumberValue.value)
      .then(() => {
        if (!orderNumberError.value) {
          if (props.events) {
            trackEvent(props.events.OrderNumber)
          }

          emit('orderNumberChanged', orderNumberValue.value)
        }
      })
  }
}

const onCustomerOrderIdChanged = () => {
  if (!props.readOnly) {
    if (props.events) {
      trackEvent(props.events.CustomerOrderNumber)
    }

    emit('customerOrderIdChanged', customerOrderIdValue.value)
  }
}

watch(
  () => props.orderNumber,
  (newValue) => {
    orderNumberValue.value = newValue
  }
)
</script>

<style lang="scss" scoped>
.input-italic {
  &:deep(input) {
    font-style: italic;
  }
}
</style>
